import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Layout,
  Flex,
  Dropdown,
  Button,
  Tabs,
  Empty,
  message,
} from "antd";
import {
  EditOutlined,
  CopyOutlined,
  DeleteOutlined,
  FileTextOutlined,
  MoreOutlined,
} from "@ant-design/icons";

import { formatDateTime } from "../../utils/appUtils";
import Loader from "../../components/common/Loader";
import PageLayout from "../../components/common/Layouts/PageLayout";
import HelmetWrapper from "../../components/common/Layouts/HelmetWrapper";
import ImageCardDisplay from "../../components/common/ImageCardDisplay";
import {
  selectMyEvents,
  selectFetchAllEventsStatus,
  selectFetchAllEventsError,
  deleteEvent,
  fetchMyEvents,
} from "../../redux/eventSlice";

const { Content } = Layout;

/**
 * Formats dates to readable format: "DD MMM YYYY | hh:mm A"
 */
const mapApiEventToUIEvent = (apiEvent) => {
  // Helper to safely extract name from object or return string as-is
  const getStringValue = (value, fallback = "") => {
    if (!value) return fallback;
    if (typeof value === 'string') return value;
    if (typeof value === 'object') return value.name || value.event_type_name || value.venue_name || fallback;
    return fallback;
  };

  return {
    id: apiEvent.id,
    title: apiEvent.event_name || "Untitled Event",
    eventType: getStringValue(apiEvent.event_type, "Event"),
    location: getStringValue(apiEvent.event_venue, ""),
    address: getStringValue(apiEvent.event_venue, ""),
    startDate: formatDateTime(apiEvent.event_start_date_time),
    endDate: formatDateTime(apiEvent.event_end_date_time),
    media: apiEvent.media,
  };
};

export default function EventsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux selectors
  const rawEventsData = useSelector(selectMyEvents);
  const loading = useSelector(selectFetchAllEventsStatus);
  const error = useSelector(selectFetchAllEventsError);

  // Local state for soft-deleted events (optimistic delete)
  const [deletedEventIds, setDeletedEventIds] = useState([]);

  // Fetch events on component mount
  useEffect(() => {
    dispatch(fetchMyEvents());
  }, [dispatch]);

  // Show error message if fetch fails
  useEffect(() => {
    if (error) {
      message.error(error);
    }
  }, [error]);

  // Transform API events to UI format
  const eventsData = useMemo(() => {
    if (!rawEventsData?.data || !Array.isArray(rawEventsData.data)) {
      return [];
    }
    return rawEventsData.data
      .filter(event => !deletedEventIds.includes(event.id)) // Filter out soft-deleted
      .map(mapApiEventToUIEvent);
  }, [rawEventsData, deletedEventIds]);

  /**
   * Immediately removes event from UI regardless of API response
   */
  const handleDeleteEvent = (eventId, eventTitle) => {
    console.log('🗑️ Deleting event:', eventId, eventTitle);
    // Call backend delete API
    dispatch(deleteEvent(eventId));
    // Soft delete - remove from UI immediately
    setDeletedEventIds(prev => [...prev, eventId]);
    message.success(`Event "${eventTitle}" deleted successfully`);
  };

  const getMenuItems = (eventId) => [
    {
      key: "view",
      icon: <FileTextOutlined />,
      label: "View Details",
      onClick: () => {
        const rawEvent = rawEventsData?.data?.find(e => e.id === eventId);
        if (rawEvent?.slug) {
          navigate(`/event-details/${rawEvent.slug}`, { state: { event: rawEvent } });
        }
      },
    },
    {
      key: "edit",
      icon: <EditOutlined />,
      label: "Edit",
      onClick: () => navigate(`/edit-event/${eventId}`),
    },
    {
      key: "duplicate",
      icon: <CopyOutlined />,
      label: "Duplicate",
      onClick: () => console.log("Duplicate", eventId),
    },
    {
      key: "delete",
      icon: <DeleteOutlined />,
      label: "Delete",
      danger: true,
      onClick: () => handleDeleteEvent(eventId, eventsData.find(e => e.id === eventId)?.title || 'Event'),
    },
  ];

  const gotoCreateEventPage = () => {
    navigate("/create-new-event");
  };

  return (
    <PageLayout title="My Events">
      {/* SEO HELMET WRAPPER */}
      <HelmetWrapper
          title="Pinginvite - The Go-To Platform for Local Events in Kolkata & Dubai" 
          description="Pingvite connects people with local events in Kolkata and Dubai through community focus, affordability, 
          and city-specific visibility for organizers and users." 
          keywords="Top Upcoming Events in Kolkata, Best Live Events in Kolkata, best upcoming events in Kolkata, 
          online events near you in Kolkata,  upcoming events in Kolkata, Events in Kolkata, Top Upcoming Events in Dubai, 
          Best Live Events in Dubai, best upcoming events in Dubai, online events near you in Dubai, Event calendar in Dubai, 
          Event tickets in Dubai, upcoming events in Dubai, Events in Dubai"
      />
      <Content className="contentWrap">
        <div className="relative">
          <div className="createBtnBlk">
            <Button
              type="primary"
              size="large"
              className="btnGradient"
              onClick={gotoCreateEventPage}
            >
              Create Event
            </Button>
          </div>
          <Tabs
            defaultActiveKey="all"
            className="eventTabs"
            type="card"
            items={[
              {
                key: "all",
                label: "All Events",
                children: (
                  <div className="tabContentWrap">
                    {loading === "loading" ? (
                      <div className="text-center p-50" style={{ textAlign: "center", padding: "50px" }}>
                        <Loader tip="Loading events..." />
                      </div>
                    ) : (
                      eventsData && eventsData.length > 0 ? (
                        <Row gutter={[20, 20]}>
                          {eventsData.map((event) => (
                            <Col xs={24} sm={24} md={12} lg={12} key={event.id}>
                              <Card hoverable className="eventCard">
                                <Dropdown
                                  menu={{ items: getMenuItems(event.id) }}
                                  trigger={["click"]}
                                  placement="bottomRight"
                                >
                                  <Button
                                    icon={<MoreOutlined />}
                                    size="small"
                                    type="text"
                                    className="eventActionMenu noStyleBtn"
                                  />
                                </Dropdown>

                                <div className="eventHeader">
                                  <div className="eventImgBlk">
                                    <ImageCardDisplay cardElement={event} isCard={true} />
                                    <Button className="eventBtnOnImg yellowBtn">
                                      {event.eventType}
                                    </Button>
                                  </div>
                                  <Flex
                                    vertical
                                    gap={8}
                                    className="eventInfo"
                                    justify="center"
                                  >
                                    <Flex vertical gap={8}>
                                      <h3 className="eventTitle">{event.title}</h3>
                                      <Flex vertical>
                                        <p className="eventLocation">
                                          {event.location}
                                        </p>
                                        <p className="eventAddress">
                                          {event.address}
                                        </p>
                                      </Flex>
                                      <div className="eventDates">
                                        <div className="dateItem">
                                          <span className="dateLabel">Start</span>
                                          <span className="dateValue">
                                            {event.startDate}
                                          </span>
                                        </div>
                                        <div className="dateItem">
                                          <span className="dateLabel">End</span>
                                          <span className="dateValue">
                                            {event.endDate}
                                          </span>
                                        </div>
                                      </div>
                                    </Flex>
                                  </Flex>
                                </div>
                              </Card>
                            </Col>
                          ))}
                        </Row>
                      ) : (
                        <Empty description="No events found" />
                      )
                    )}
                  </div>
                ),
              },
              {
                key: "upcoming",
                label: "Upcoming",
                children: <div className="tabContentWrap">Upcoming</div>,
              },
              {
                key: "completed",
                label: "Completed",
                children: <div className="tabContentWrap">Completed</div>,
              },
              {
                key: "draft",
                label: "Draft",
                children: <div className="tabContentWrap">Draft</div>,
              },
            ]}
          />
        </div>
      </Content>
    </PageLayout>
  );
}
